import styled from '@emotion/styled';
import { ChangeEvent, FocusEvent, ForwardedRef, forwardRef } from 'react';
import { FlexContainer, FlexItem } from '../FlexLayout';
import { FieldContainerProps } from '../../NitAnalysisCard/models/Props';
import { FieldValue } from '../../NitAnalysisCard/styles/styles';
import { colors } from '../styles/colors';

const StyledInput = styled.input<{ $showMaxLengthError: boolean }>`
  border-color: ${({ $showMaxLengthError }) =>
    $showMaxLengthError ? colors.red.primary : colors.gray.primary};
  box-shadow: ${({ $showMaxLengthError }) =>
    $showMaxLengthError ? `0 0 0 1px ${colors.red.primary}` : 'none'};
`;

const ValidationText = styled.div`
  color: ${colors.red.primary};
  font-size: 12px;
  margin-top: 4px;
`;

export const FieldContainer = forwardRef(
  (
    {
      id,
      title,
      value,
      onChange,
      onBlur,
      maxLength,
      placeholder,
      tabIndex: index,
      className,
      disabled = false,
      autofocus = false,
      onFocus,
      required = true,
    }: FieldContainerProps,
    ref: ForwardedRef<HTMLInputElement>
  ) => {
    const showMaxLengthError =
      typeof maxLength === 'number' && maxLength >= 0 && (value || '').length > maxLength;
    const validationTextId = `${id}-validation-error`;

    const validateMaxLength = (input: HTMLInputElement) => {
      if (typeof maxLength !== 'number' || maxLength < 0) {
        input.setCustomValidity('');
        return;
      }

      if (input.value.length > maxLength) {
        input.setCustomValidity(`Max ${maxLength} tecken tillatna`);
      } else {
        input.setCustomValidity('');
      }
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      validateMaxLength(event.currentTarget);
      onChange?.(event);
    };

    const handleBlur = async (event: FocusEvent<HTMLInputElement>) => {
      validateMaxLength(event.currentTarget);
      await onBlur?.(event);
    };

    return (
      <FieldValue>
        <FlexContainer justify="space-between">
          <FlexItem width={50}>
            <label htmlFor={id}>{title}</label>
          </FlexItem>
          <FlexItem width={50}>
            <StyledInput
              type="text"
              id={id}
              name={id}
              placeholder={placeholder}
              value={value || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              required={required}
              autoFocus={autofocus}
              tabIndex={index}
              className={className}
              disabled={disabled}
              aria-invalid={showMaxLengthError}
              aria-describedby={showMaxLengthError ? validationTextId : undefined}
              $showMaxLengthError={showMaxLengthError}
              onFocus={e => onFocus && onFocus(e, id)}
              ref={ref}
            />
            {showMaxLengthError && (
              <ValidationText id={validationTextId} role="alert">
                Max {maxLength} tecken.
              </ValidationText>
            )}
          </FlexItem>
        </FlexContainer>
      </FieldValue>
    );
  }
);
